import { Box, Code, Paragraph, Select, Strong, Tooltip, TooltipContent } from '@customerio/pluma-components/react';
import type { ObjectType } from '@/services/db/object-type/entity';
import { ImportType } from '@/services/db/sql-import/entity';
import type { SqlDatabaseType } from '@/services/db/sql-import-database/entity';
import { QueryEditor, type SqlImportRelationship, type usePreviewRun } from '@/features/sql-sync';
import type { SqlImportDraft } from '../draft';
import { relationshipCopy } from '../relationships';
import { PEOPLE_ID_FIELDS } from '../settings';

type RelationshipFieldsProps = {
  isObjectImport: boolean;
  relationship: SqlImportRelationship;
  objectTypes: readonly Pick<ObjectType, 'id' | 'name' | 'enabled'>[];
  canIdentifyByEmail: boolean;
  isReadOnly: boolean;
  /** The related object type is fixed: `update_query` cannot carry it. See `isObjectTypeLocked`. */
  isObjectTypeLocked?: boolean;
  onChange: (changes: Partial<SqlImportRelationship>) => void;
};

const OBJECT_TYPE_LOCKED_HINT =
  "The object type these people relate to can't change here. Change it on the sync's Settings tab.";

/**
 * Which side still needs choosing: an object import picks how its related people are identified;
 * a people import picks which object type the people relate to.
 */
function RelationshipFields({
  isObjectImport,
  relationship,
  objectTypes,
  canIdentifyByEmail,
  isReadOnly,
  isObjectTypeLocked = false,
  onChange,
}: RelationshipFieldsProps) {
  if (isObjectImport) {
    const label = 'How do you want to identify people?';
    return (
      <Box mb="200">
        <Tooltip isDisabled={canIdentifyByEmail} isCustom={true}>
          {(context) => (
            <>
              <Select
                ref={context.reference.setReference}
                {...context.reference.getReferenceProps()}
                label={label}
                placeholder="Select"
                options={PEOPLE_ID_FIELDS}
                value={relationship.entity1_id_field_name}
                isDisabled={isReadOnly || !canIdentifyByEmail}
                data-test-create-sql-import-select="update-people-by"
                onChange={(value) => {
                  if (value != null && value !== relationship.entity1_id_field_name) {
                    onChange({ entity1_id_field_name: value });
                  }
                }}
              />
              <TooltipContent>
                <Paragraph color="inherit">
                  <Code>id</Code> will be used to match sql rows to people
                </Paragraph>
              </TooltipContent>
            </>
          )}
        </Tooltip>
      </Box>
    );
  }
  return (
    <Box mb="200">
      <Select
        label="Choose object type"
        placeholder="Select"
        options={objectTypes
          .filter((objectType) => objectType.enabled)
          .map((objectType) => ({ value: objectType.id, label: objectType.name }))}
        value={relationship.entity2_type_id === 0 ? null : String(relationship.entity2_type_id)}
        isDisabled={isReadOnly || isObjectTypeLocked}
        data-test-create-sql-import-select="related-to-object-type"
        onChange={(value) => {
          if (value != null && Number(value) !== relationship.entity2_type_id) {
            onChange({ entity2_type_id: Number(value) });
          }
        }}
      />
      {isObjectTypeLocked ? (
        <Paragraph color="subtle" mt="050" data-test-sql-import-object-type-locked>
          {OBJECT_TYPE_LOCKED_HINT}
        </Paragraph>
      ) : null}
    </Box>
  );
}

type RelationshipsStepProps = {
  type: SqlDatabaseType;
  draft: SqlImportDraft;
  relationship: SqlImportRelationship;
  objectTypes: readonly Pick<ObjectType, 'id' | 'name' | 'enabled'>[];
  isReadOnly: boolean;
  canIdentifyByEmail: boolean;
  /**
   * Show the related object type as fixed. The detail's Relationship Query tab saves through
   * `update_query`, whose relationship half carries only the query and the people identifier
   * (`ui_api/sql_imports/update_query.go`), so a change here could not be sent.
   */
  isObjectTypeLocked?: boolean;
  /** Why "Run query" is off for a reason the editor cannot see, or null. */
  blockedReason?: string | null;
  run: ReturnType<typeof usePreviewRun>;
  onChange: (changes: Partial<SqlImportRelationship>) => void;
  onRun: () => void;
  onTimeoutDismiss: () => void;
  onTimeoutContinue: () => void;
};

/** The optional Relationships step: a second query relating the imported rows to people or objects. */
export function RelationshipsStep({
  type,
  draft,
  relationship,
  objectTypes,
  isReadOnly,
  canIdentifyByEmail,
  isObjectTypeLocked = false,
  blockedReason = null,
  run,
  onChange,
  onRun,
  onTimeoutDismiss,
  onTimeoutContinue,
}: RelationshipsStepProps) {
  const isObjectImport = draft.import_type === ImportType.object;
  const relatedObjectType = objectTypes.find(
    (objectType) => objectType.id === String(isObjectImport ? draft.object_type_id : relationship.entity2_type_id),
  );
  const copy = relationshipCopy(isObjectImport, relatedObjectType?.name, { singular: 'person', plural: 'people' });

  return (
    <QueryEditor
      databaseType={type}
      query={relationship.query}
      onQueryChange={(query) => onChange({ query })}
      location={draft.location}
      header={
        <>
          <Paragraph mb="200">
            <Strong>{copy.title}</Strong> Write a query returning the relationships to sync.
          </Paragraph>
          <Paragraph mb="200">
            {copy.subtitle} Optionally, you may include a boolean <Code>deleted</Code> column for any relationships we
            should delete.
          </Paragraph>
          <RelationshipFields
            isObjectImport={isObjectImport}
            relationship={relationship}
            objectTypes={objectTypes}
            canIdentifyByEmail={canIdentifyByEmail}
            isReadOnly={isReadOnly}
            isObjectTypeLocked={isObjectTypeLocked}
            onChange={onChange}
          />
        </>
      }
      isReadOnly={isReadOnly}
      canIdentifyByEmail={canIdentifyByEmail}
      blockedReason={blockedReason}
      run={run}
      onRun={onRun}
      onTimeoutDismiss={onTimeoutDismiss}
      onTimeoutContinue={onTimeoutContinue}
    />
  );
}
